import React, { useState } from 'react';
import { Checkbox, Collapse, FormControlLabel, Grid, IconButton, Stack } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    ElectricalCheckboxKey,
    ElectricalPropertyKey,
    ElectricalSelectKey,
    ElectricalTextKey,
    NHC_MAX_UNIT_KEY,
} from 'src/api/project-settings-keys';
import { GetProjectSettingsResponse } from 'src/model/api/v2/data-contracts';
import { useSetElectricalPropertyMutation } from 'src/api/v1/project-settings-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { SettingsDetailsToggle } from 'src/pages/ProjectSettingsPage/components/SettingsDetailsToggle';
import { SettingsSelect } from 'src/pages/ProjectSettingsPage/components/SettingsSelect';
import { SettingsNumberInput } from 'src/pages/ProjectSettingsPage/components/SettingsNumberInput';
import { SettingsTextInput } from 'src/pages/ProjectSettingsPage/components/SettingsTextInput';
import { useSaveFeedback } from 'src/pages/ProjectSettingsPage/useSaveFeedback';
import { ts } from 'src/utils/translate';
import { DOUBLE_SPACING } from 'src/theme/style-consts';

type Props = {
    projectId: number;
    settings: GetProjectSettingsResponse;
    canEdit: boolean;
};

const EXPLANATION_URL_HU = 'https://uplan.io/hu/elektromos-rendszerek/';
const EXPLANATION_URL_DEFAULT = 'https://uplan.io/electrical-properties';
const NHC_MAX_UNIT_MIN = 1;

export const ElectricalPropertiesSection = ({ projectId, settings, canEdit }: Props) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [setElectricalProperty] = useSetElectricalPropertyMutation();
    const { showSaveSuccess, showSaveError } = useSaveFeedback();

    const saveElectricalProperty = (key: ElectricalPropertyKey, value: number | string | boolean) => {
        setElectricalProperty({ projectId, key, value })
            .unwrap()
            .then(showSaveSuccess)
            .catch(showSaveError);
    };

    const isCheckboxChecked = (key: ElectricalCheckboxKey) => settings.electrical[key];

    const toggleDetails = () => setDetailsOpen(!detailsOpen);

    const renderSelect = (key: ElectricalSelectKey, label: string, hideLabel = false) => (
        <SettingsSelect
            id={key}
            label={label}
            hideLabel={hideLabel}
            value={settings.electrical[key]}
            options={settings.options.electrical[key]}
            disabled={!canEdit}
            onChange={(value) => saveElectricalProperty(key, value)}
        />
    );

    const renderCheckbox = (key: ElectricalCheckboxKey, label: string) => (
        <FormControlLabel
            label={label}
            disabled={!canEdit}
            control={
                <Checkbox
                    id={key}
                    size="small"
                    checked={settings.electrical[key]}
                    onChange={(event) => saveElectricalProperty(key, event.target.checked)}
                />
            }
        />
    );

    const renderText = (key: ElectricalTextKey, label: string, multiline = false) => (
        <SettingsTextInput
            id={key}
            label={label}
            value={settings.electrical[key]}
            disabled={!canEdit}
            multiline={multiline}
            onSave={(value) => saveElectricalProperty(key, value)}
        />
    );

    const renderCheckboxWithSelect = (
        checkboxKey: ElectricalCheckboxKey,
        selectKey: ElectricalSelectKey | null,
        label: string,
    ) => (
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid size={6}>{renderCheckbox(checkboxKey, label)}</Grid>
            <Grid size={6}>
                {selectKey != null && isCheckboxChecked(checkboxKey) && renderSelect(selectKey, label, true)}
            </Grid>
        </Grid>
    );

    const typeLabel = ts('project_settings_electrical_type', 'Type');
    const explanationUrl = ts('site_lang', 'hu-HU') === 'hu-HU' ? EXPLANATION_URL_HU : EXPLANATION_URL_DEFAULT;

    return (
        <SettingsSection
            title={ts('project_settings_electrical_title', 'Electrical properties')}
            titleAction={
                <Tooltip title={ts('project_settings_electrical_explanation', 'Click here to view electrical settings explanation')}>
                    <IconButton size="small" href={explanationUrl} target="_blank" rel="noreferrer">
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            }
        >
            <Grid container spacing={3} sx={{ alignItems: 'center' }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    {renderSelect('supply_type_id', ts('project_settings_electrical_supply', 'Supply'))}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" spacing={2}>
                        {renderCheckbox('supply_as_terminal_block_check', ts('project_settings_electrical_draw_as_terminal', 'draw as a terminal'))}
                        {renderCheckbox('service_socket', ts('project_settings_electrical_service_socket', 'service socket'))}
                    </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    {renderSelect('supply_cable_type_id', ts('project_settings_electrical_cable_type', 'Cable type'))}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    {renderSelect('supply_profile_id', ts('project_settings_electrical_profile', 'Profile'))}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    {renderText('supply_label', ts('project_settings_electrical_label', 'Label'))}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    {renderText('supply_note', ts('project_settings_electrical_note', 'Note'), true)}
                </Grid>
            </Grid>

            <Collapse in={detailsOpen}>
                <Grid container spacing={3} sx={{ alignItems: 'center', marginTop: DOUBLE_SPACING }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderCheckboxWithSelect(
                            'main_switch_check',
                            'main_switch_value_id',
                            ts('project_settings_electrical_main_switch', 'Main switch'),
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {isCheckboxChecked('main_switch_check') && renderSelect('main_switch_type_id', typeLabel)}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {renderCheckboxWithSelect(
                            'overvoltage_protection_check',
                            null,
                            ts('project_settings_electrical_overvoltage_protection', 'Overvoltage protection'),
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {isCheckboxChecked('overvoltage_protection_check') && renderSelect('overvoltage_protection_type_id', typeLabel)}
                    </Grid>
                    {isCheckboxChecked('overvoltage_protection_check') && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            {renderCheckboxWithSelect(
                                'protection_before_overvoltage_protection_check',
                                'protection_before_overvoltage_protection_value_id',
                                ts('project_settings_electrical_protection_before_overvoltage', 'Protection before overvoltage protection'),
                            )}
                        </Grid>
                    )}
                    {settings.show_nhc_max_unit_number_in_row && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SettingsNumberInput
                                id={NHC_MAX_UNIT_KEY}
                                label={ts('project_settings_electrical_nhc_max_unit', 'Niko Home Controller Max Din Device Unit Number In Row')}
                                value={settings.electrical.nhc_max_unit_number_in_row ?? null}
                                min={NHC_MAX_UNIT_MIN}
                                disabled={!canEdit}
                                onSave={(value) => saveElectricalProperty(NHC_MAX_UNIT_KEY, value)}
                            />
                        </Grid>
                    )}
                </Grid>
            </Collapse>

            <SettingsDetailsToggle open={detailsOpen} onToggle={toggleDetails} />
        </SettingsSection>
    );
};
